import { Address, ErrorResponse, ServiceResponse } from '@bzbs/react-api-client';
import React, { createContext, useContext, useState } from 'react';
import { useBuzzebeesServiceContext } from './buzzebees_service_provider';

export type AddressContextType = {
  addresses: Address[];
  taxAddresses: Address[];
  isLoading: boolean;
  error: ErrorResponse | null;
  actions: {
    fetchAddresses: () => Promise<ServiceResponse<Address[]>>;
    addAddress: (address: AddressFormData) => Promise<ServiceResponse<Address>>;
    editAddress: (address: AddressFormData) => Promise<ServiceResponse<Address>>;
    deleteAddress: (rowKey: string) => Promise<ServiceResponse<any>>;
    fetchTaxAddresses: () => Promise<ServiceResponse<Address[]>>;
    addTaxAddress: (address: AddressFormData) => Promise<ServiceResponse<Address>>;
    editTaxAddress: (address: AddressFormData) => Promise<ServiceResponse<Address>>;
    deleteTaxAddress: (rowKey: string) => Promise<ServiceResponse<any>>;
  };
};

export type AddressFormData = {
  addressName?: string;
  firstName?: string;
  lastName?: string;
  address?: string;
  zipcode?: string;
  provinceCode?: string;
  provinceName?: string;
  districtCode?: string;
  districtName?: string;
  subDistrictCode?: string;
  subDistrictName?: string;
  contactNumber?: string;
  countryCode?: string;
  countryName?: string;
  isDefault?: boolean;
  rowKey?: string;
};

const AddressContext = createContext<AddressContextType>({
  addresses: [],
  taxAddresses: [],
  isLoading: false,
  error: null,
  actions: {
    fetchAddresses: async () => ({ type: 'client-error', message: 'Not implemented' }),
    addAddress: async () => ({ type: 'client-error', message: 'Not implemented' }),
    editAddress: async () => ({ type: 'client-error', message: 'Not implemented' }),
    deleteAddress: async () => ({ type: 'client-error', message: 'Not implemented' }),
    fetchTaxAddresses: async () => ({ type: 'client-error', message: 'Not implemented' }),
    addTaxAddress: async () => ({ type: 'client-error', message: 'Not implemented' }),
    editTaxAddress: async () => ({ type: 'client-error', message: 'Not implemented' }),
    deleteTaxAddress: async () => ({ type: 'client-error', message: 'Not implemented' }),
  },
});

export function useAddressContext() {
  return useContext(AddressContext);
}

export function AddressProvider({ children }: { children: React.ReactNode }) {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [taxAddresses, setTaxAddresses] = useState<Address[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);

  const { bzbsService } = useBuzzebeesServiceContext();

  const fetchAddresses = async (): Promise<ServiceResponse<Address[]>> => {
    setIsLoading(true);
    const response = await bzbsService.addressApi.userAddresses();
    setIsLoading(false);

    if (response.type === 'success') {
      setAddresses(response.model);
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const addAddress = async (addressForm: AddressFormData): Promise<ServiceResponse<Address>> => {
    setIsLoading(true);
    const response = await bzbsService.addressApi.updateAddress({
      ...addressForm,
      rowKey: '',
    });
    setIsLoading(false);

    if (response.type === 'success') {
      fetchAddresses();
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const editAddress = async (addressForm: AddressFormData): Promise<ServiceResponse<Address>> => {
    setIsLoading(true);
    const response = await bzbsService.addressApi.updateAddress({
      ...addressForm,
    });
    setIsLoading(false);

    if (response.type === 'success') {
      fetchAddresses();
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const deleteAddress = async (rowKey: string): Promise<ServiceResponse<any>> => {
    setIsLoading(true);
    const response = await bzbsService.addressApi.deleteAddress({ rowKey });
    setIsLoading(false);

    if (response.type === 'success') {
      fetchAddresses();
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const fetchTaxAddresses = async (): Promise<ServiceResponse<Address[]>> => {
    setIsLoading(true);
    const response = await bzbsService.addressApi.userTaxAddresses();
    setIsLoading(false);

    if (response.type === 'success') {
      setTaxAddresses(response.model);
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const addTaxAddress = async (addressForm: AddressFormData): Promise<ServiceResponse<Address>> => {
    setIsLoading(true);
    const response = await bzbsService.addressApi.updateTaxAddress({
      ...addressForm,
      rowKey: '',
    });
    setIsLoading(false);

    if (response.type === 'success') {
      fetchTaxAddresses();
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const editTaxAddress = async (addressForm: AddressFormData): Promise<ServiceResponse<Address>> => {
    setIsLoading(true);
    const response = await bzbsService.addressApi.updateTaxAddress({
      ...addressForm,
      rowKey: addressForm.rowKey || '',
    });
    setIsLoading(false);

    if (response.type === 'success') {
      fetchTaxAddresses();
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const deleteTaxAddress = async (rowKey: string): Promise<ServiceResponse<any>> => {
    setIsLoading(true);
    const response = await bzbsService.addressApi.deleteTaxAddress({ rowKey });
    setIsLoading(false);

    if (response.type === 'success') {
      fetchTaxAddresses();
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  return (
    <AddressContext.Provider
      value={{
        addresses,
        taxAddresses,
        isLoading,
        error,
        actions: {
          fetchAddresses,
          addAddress,
          editAddress,
          deleteAddress,
          fetchTaxAddresses,
          addTaxAddress,
          editTaxAddress,
          deleteTaxAddress,
        },
      }}
    >
      {children}
    </AddressContext.Provider>
  );
}
